import React, { useMemo } from "react";
import { Container, Col, Row } from "reactstrap";
import { useQuery } from "@apollo/client";
import _get from "lodash/get";
import { Component as Form } from "@repoflow/form-schema";
import * as Notify from "@repoflow/form-schema/notify";
import * as Loading from "@repoflow/components/loading";
import * as Section from "@repoflow/components/section";
import { Mutation, Button, useSectionContext } from "@repoflow/components/section";
import * as widgets from "widgets/register";
import * as SiteQL from "queries/site";
import * as BlogQL from "queries/blog";
import SitePublicSchema from "@common/schema/site/public";

// THE SITE'S AUTHORS, as a form over the site's `blog` field.
//
// One save, through the site's own update (the field lives on the site row —
// see @common/schema/site/fields), with the title sent along because the
// update requires it. The form targets /Site/Blog directly, so the screen is
// the authors and nothing else; the same "keep it flat" trick the site's
// Configuration tab plays with /PageConfig.
const TARGET = { $ref: "/Site/Blog" };
const MUTATION_TARGET = { $ref: "/Public/Site/Update" };

export const AUTHORS_FIELDS = {
  "/Site/Blog": {
    fields: [{ property: "authors", layout: "wide" }],
  },
  "/Site/Blog/Author": {
    fields: ["authorId", "name", { property: "bio", layout: "wide" }, { property: "image", layout: "wide" }],
    listing: ["authorId", "name"],
  },
};

export const Sidebar = () => null;
export const Indice = () => null;

export const Content = ({
  match: {
    params: { siteId },
  },
}) => {
  const { parent, base, basepath, attributes } = useSectionContext();
  const site = parent && parent.values;

  const { data, loading } = useQuery(BlogQL.authors, {
    variables: { siteId },
    fetchPolicy: "cache-and-network",
  });
  const authors = useMemo(
    () => _get(data, "viewer.account.sites.get.blog.authors") || [],
    [data]
  );
  const initial = useMemo(
    () => ({ title: (site && site.title) || "", blog: { authors } }),
    [site, authors]
  );

  if (loading && !data) {
    return <Loading.Content />;
  }

  // The SITE's schema, not the blog section's: the mutation targets
  // /Public/Site/Update and the form /Site/Blog, and both live on the site's
  // public schema. Under the blog provider — whose schema is the post's — the
  // target was unresolved and the form had no values (measured on br-w01).
  return (
    <Section.Provider
      schema={SitePublicSchema}
      base={base}
      basepath={basepath}
      attributes={attributes}
    >
      <Mutation
      key={JSON.stringify(authors)}
      initial={initial}
      target={MUTATION_TARGET}
      mutation={SiteQL.edit}
      update={BlogQL.update()}
      onMutate={({ handler, state: { values } }) => {
        handler({ variables: { siteId, input: { title: values.title, blog: values.blog } } });
      }}
      onCompleted={() => {
        Notify.success("Authors saved");
      }}
      onError={(error) => {
        Notify.error(error.toString());
      }}
    >
        {MutationHandler}
      </Mutation>
    </Section.Provider>
  );
};

const MutationHandler = ({ state, mutate }) => {
  const { values, setValues } = state;

  return (
    <Container className="mt-4">
      <Row>
        <Col>
          <Form
            schema={SitePublicSchema}
            target={TARGET}
            widgets={widgets}
            values={values.blog || { authors: [] }}
            onChange={(mod) => setValues({ ...values, blog: mod })}
            onSubmit={() => mutate()}
            config={AUTHORS_FIELDS}
          >
            <Button type="submit">Save</Button>
          </Form>
        </Col>
      </Row>
    </Container>
  );
};
